import React from 'react';
import { generateWebPWithFallback } from '@/utils/imageUtils';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { sanitizeHtml } from '@/lib/sanitizeHtml';

export interface AboutBlockProps {
  title: string;
  subtitle?: string;
  content: string; // HTML content or simple text
  imageUrl?: string;
  imagePosition?: 'left' | 'right';
}

export const AboutBlock: React.FC<AboutBlockProps> = ({
  title,
  subtitle,
  content,
  imageUrl,
  imagePosition = 'right',
}) => {
  if (!title && !content) return null;

  const imgSrc = imageUrl ? generateWebPWithFallback(imageUrl, 82, 800, 800).webp : '';
  const cleanContent = sanitizeHtml(content);

  const TextContent = () => (
    <ScrollReveal animation="up" delayMs={100} className="flex flex-col justify-center">
      {subtitle && (
        <span className="text-[var(--brand-primary)] font-semibold text-sm uppercase tracking-wider mb-2">
          {subtitle}
        </span>
      )}
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-6">
        {title}
      </h2>
      <div
        className="prose prose-lg text-muted-foreground prose-p:leading-relaxed"
        dangerouslySetInnerHTML={{ __html: cleanContent }}
      />
    </ScrollReveal>
  );

  const ImageContent = () => {
    if (!imgSrc) return null;
    return (
      <ScrollReveal animation="scale" delayMs={200} className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] rounded-2xl overflow-hidden shadow-2xl">
        <img
          src={imgSrc}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
        {/* Soft gradient overlay for premium feel */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      </ScrollReveal>
    );
  };

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {!imgSrc ? (
          <ScrollReveal animation="up" className="max-w-3xl mx-auto text-center">
            {subtitle && (
              <span className="text-[var(--brand-primary)] font-semibold text-sm uppercase tracking-wider mb-2 block">
                {subtitle}
              </span>
            )}
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-8">
              {title}
            </h2>
            <div
              className="prose prose-lg text-muted-foreground mx-auto text-left"
              dangerouslySetInnerHTML={{ __html: cleanContent }}
            />
          </ScrollReveal>
        ) : (
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${imagePosition === 'left' ? '' : 'lg:grid-flow-col-dense'
            }`}>
            {imagePosition === 'left' ? (
              <>
                <ImageContent />
                <TextContent />
              </>
            ) : (
              <>
                <TextContent />
                <ImageContent />
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
